import type { KarlAsk, KarlAskAction } from "../karlAsks";
import type { AskAnswerEffect, AskAnswerPlan, KarlAskArm } from "./types";

/**
 * The roster keys a board by its marker label ("PA") while the caption reads
 * its full name ("PAS/HES · Erdung"), so confirming by caption settles nothing.
 */
const confirmKeyFor = (ask: KarlAsk): string => ask.subjectKey ?? ask.subject ?? ask.id;

// `committed` is what the outcome becomes once the plan click lands; the
// reducer records nothing until then, so the field is only read on commit.
const armingPlan = (arm: KarlAskArm): AskAnswerPlan => ({
  arm,
  outcome: "committed",
  effects: [{ kind: "arm", arm }],
  isDeferral: false,
});

export const askAnswerEffect: AskAnswerEffect = ({ ask, answer }) => {
  const action: KarlAskAction = answer.action;

  switch (action.kind) {
    case "accept": {
      const key = confirmKeyFor(ask);
      return {
        arm: null,
        outcome: "accepted",
        confirmKey: key,
        revert: { kind: "unconfirm", key },
        effects: [{ kind: "confirm", key }],
        isDeferral: false,
      };
    }

    case "setMainBoard": {
      const key = confirmKeyFor(ask);
      return {
        arm: null,
        outcome: "accepted",
        confirmKey: key,
        revert: { kind: "clearMainBoard" },
        effects: [{ kind: "setMainBoard", label: action.label }, { kind: "confirm", key }],
        isDeferral: false,
      };
    }

    case "pickMainBoard":
      return armingPlan({ kind: "pickMainBoard", placesAs: action.placesAs });

    case "setBoardFeeds": {
      const key = confirmKeyFor(ask);
      return {
        arm: null,
        outcome: "accepted",
        confirmKey: key,
        revert: { kind: "clearBoardFeeds", label: action.label },
        effects: [
          { kind: "setBoardFeeds", label: action.label, feed: action.feed },
          { kind: "confirm", key },
        ],
        isDeferral: false,
      };
    }

    case "setHeight": {
      const key = confirmKeyFor(ask);
      return {
        arm: null,
        outcome: "accepted",
        confirmKey: key,
        revert: { kind: "clearHeight", target: action.target },
        effects: [
          { kind: "setHeight", target: action.target, heightM: action.heightM },
          { kind: "confirm", key },
        ],
        isDeferral: false,
      };
    }

    case "setFeederFrom": {
      const key = confirmKeyFor(ask);
      return {
        arm: null,
        outcome: "accepted",
        confirmKey: key,
        revert: { kind: "clearFeederFrom", toLabel: action.toLabel },
        effects: [
          { kind: "setFeederFrom", toLabel: action.toLabel, fromLabel: action.fromLabel },
          { kind: "confirm", key },
        ],
        isDeferral: false,
      };
    }

    case "pickUpstreamFor":
      return armingPlan({ kind: "pickUpstreamFor", toLabel: action.toLabel });

    case "setFeederOffFloor": {
      const key = confirmKeyFor(ask);
      return {
        arm: null,
        outcome: "accepted",
        confirmKey: key,
        revert: { kind: "feederOnFloor", toLabel: action.toLabel },
        effects: [{ kind: "setFeederOffFloor", toLabel: action.toLabel }, { kind: "confirm", key }],
        isDeferral: false,
      };
    }

    case "setFeederType": {
      const key = confirmKeyFor(ask);
      return {
        arm: null,
        outcome: "accepted",
        confirmKey: key,
        revert: { kind: "clearFeederType", toLabel: action.toLabel },
        effects: [
          { kind: "setFeederType", toLabel: action.toLabel, cableType: action.cableType },
          { kind: "confirm", key },
        ],
        isDeferral: false,
      };
    }

    // The card opens the picker itself; nothing reaches the queue.
    case "editFeederType":
      return { arm: null, outcome: "declined", effects: [], isDeferral: false };

    // The card opens the entry itself; nothing reaches the queue.
    case "editHeight":
      return { arm: null, outcome: "declined", effects: [], isDeferral: false };

    // The card opens the checklist itself; nothing reaches the queue.
    case "editBoardFeeds":
      return { arm: null, outcome: "declined", effects: [], isDeferral: false };

    case "pickFeederFor":
      return armingPlan({ kind: "pickFeederFor", category: action.category });

    case "leaveCategoryUnbilled":
      return {
        arm: null,
        outcome: "accepted",
        revert: { kind: "billCategory", category: action.category },
        effects: [{ kind: "unbillCategory", category: action.category }],
        isDeferral: false,
      };

    case "setBoardRole": {
      const key = confirmKeyFor(ask);
      return {
        arm: null,
        outcome: "accepted",
        confirmKey: key,
        revert: { kind: "clearBoardRole", label: action.label },
        effects: [
          { kind: "setBoardRole", label: action.label, role: action.role },
          { kind: "confirm", key },
        ],
        isDeferral: false,
      };
    }

    // "Later" defers; it must never mean "never again".
    case "skip":
      return { arm: null, outcome: "declined", effects: [], isDeferral: true };

    case "moveSource":
      return armingPlan({ kind: "moveSource", label: action.label });

    case "placeSource":
      return armingPlan({ kind: "placeSource", name: action.name });

    case "drawBox":
      return armingPlan({
        kind: "drawBox",
        wayKey: action.wayKey,
        circuitId: action.circuitId,
      });

    case "boxAssign":
      return armingPlan({ kind: "boxAssign", circuitId: action.circuitId });

    case "boxRemove":
      return armingPlan({ kind: "boxRemove", circuitId: action.circuitId });

    case "pickRoute":
      return armingPlan({ kind: "pickRoute", groupId: action.groupId });

    case "drawZone":
      return armingPlan({ kind: "drawZone", label: action.label });

    case "removeSourceTo":
      return {
        arm: null,
        outcome: "declined",
        revert: { kind: "restoreSource", label: action.label },
        effects: [
          { kind: "removeSourceTo", label: action.label, toLabel: action.toLabel },
        ],
        isDeferral: false,
      };

    // The card takes the name itself; nothing reaches the queue.
    case "addSource":
      return { arm: null, outcome: "declined", effects: [], isDeferral: false };

    case "parkRemovalGroup":
      return {
        arm: null,
        outcome: "accepted",
        effects: [{ kind: "closeRemovalGroup", groupId: action.groupId }],
        isDeferral: false,
      };

    case "parkDevices":
      return {
        arm: null,
        outcome: "declined",
        effects: [
          {
            kind: "parkDevices",
            groupId: action.groupId,
            destinationIds: action.destinationIds,
          },
          { kind: "closeRemovalGroup", groupId: action.groupId },
        ],
        isDeferral: false,
      };

    // Parks them AND stays on screen: he is about to click the run they belong
    // on, and the ask must not move out from under him.
    case "parkAndPick": {
      const arm = { kind: "pickRoute" as const, groupId: action.groupId };
      return {
        arm,
        outcome: "committed",
        effects: [
          {
            kind: "parkDevices",
            groupId: action.groupId,
            destinationIds: action.destinationIds,
          },
          { kind: "arm", arm },
        ],
        isDeferral: false,
      };
    }

    // Nothing is armed: the mode is on until he leaves it, and the question he
    // was asked is still the question.
    case "editRouteShape":
      return {
        arm: null,
        outcome: "declined",
        effects: [{ kind: "editRouteShape", circuitId: action.circuitId }],
        isDeferral: false,
      };

    case "restoreRemovalGroup":
      return {
        arm: null,
        outcome: "accepted",
        effects: [{ kind: "restoreRemovalGroup", groupId: action.groupId }],
        isDeferral: false,
      };

    case "removeSource":
      return {
        arm: null,
        outcome: "declined",
        revert: { kind: "restoreSource", label: action.label },
        effects: [{ kind: "removeSource", label: action.label }],
        isDeferral: false,
      };

    case "restoreSource":
      return {
        arm: null,
        outcome: "accepted",
        revert: { kind: "removeSource", label: action.label },
        effects: [{ kind: "restoreSource", label: action.label }],
        isDeferral: false,
      };

    case "renameSource":
      return {
        arm: null,
        outcome: "accepted",
        effects: [{ kind: "renameSource", label: action.label }],
        isDeferral: false,
      };

    default: {
      const unhandled: never = action;
      throw new Error(`unhandled ask action: ${JSON.stringify(unhandled)}`);
    }
  }
};
